import { SessionManager } from './sessionManager'
import { buildStepPayload } from './enrollmentDataUtils'

/**
 * Navigation management utilities for enrollment wizard
 * ✅ FIXED: Removes nested step objects, uses flat field structure
 */
export class NavigationManager {
  private sessionManager: SessionManager
  private currentWizardStepIndex: number
  private setCurrentWizardStepIndex: (index: number) => void
  private isCompletedMode: boolean

  constructor(
    sessionManager: SessionManager,
    currentWizardStepIndex: number,
    setCurrentWizardStepIndex: (index: number) => void,
    isCompletedMode: boolean
  ) {
    this.sessionManager = sessionManager
    this.currentWizardStepIndex = currentWizardStepIndex
    this.setCurrentWizardStepIndex = setCurrentWizardStepIndex
    this.isCompletedMode = isCompletedMode
  }

  /**
   * Handle navigation to a target step with validation
   * ✅ FIXED: No longer creates nested step objects
   */
  async handleNavigate(
    targetIndex: number,
    enrollmentDataRef: React.MutableRefObject<any>,
    steps: any[]
  ): Promise<any> {

    // Guard completed-mode locally — no round-trip needed.
    // validateStepAccess always returns success for non-completed sessions,
    // and isCompletedMode already captures the completed state.
    if (this.isCompletedMode && targetIndex < 8) {
      alert('Only steps 9-10 (Signature & Followup) are accessible for completed applications.')
      return
    }
    
    // Check step completion restrictions
    const currentIndex = this.currentWizardStepIndex
    const enrollmentData = enrollmentDataRef.current
    const completedSteps = enrollmentData.completedSteps || []
    
    // Forward navigation - backfill all previous steps
    if (targetIndex > currentIndex) {
      const newCompletedSteps = [...completedSteps]
      const newSkippedSteps = [...(enrollmentData.skippedSteps || [])]
      
      // ✅ FIXED: Only update completion arrays, don't create nested step objects
      for (let i = currentIndex; i < targetIndex; i++) {
        if (!newCompletedSteps.includes(i) && !newSkippedSteps.includes(i)) {
          newCompletedSteps.push(i)
        }
      }
      
      // ✅ FIXED: Update only the tracking arrays, not step data
      enrollmentDataRef.current = {
        ...enrollmentData,
        completedSteps: newCompletedSteps,
        skippedSteps: newSkippedSteps
      }
    }
    
    // Validate target step index is within bounds
    if (targetIndex < 0 || targetIndex >= steps.length) {
      console.warn('⚠️ Navigation blocked: Target step index out of bounds', {
        targetIndex,
        totalSteps: steps.length,
        attemptedStep: targetIndex + 1,
        availableSteps: `1-${steps.length}`
      })
      // Just log and return silently - don't show alert for automatic navigation
      return
    }
    
    const currentStepId = steps[currentIndex].id

    try {
      // Save current step data and advance currentStepIndex to targetIndex in one DB write.
      // Passing targetIndex as newStepIndex means we no longer need a separate
      // updateCurrentStepIndex() call — the single save sets everything correctly.
      const saved = await this.sessionManager.saveCurrentStep(currentIndex, currentStepId, targetIndex)

      if (saved?.id) {
        this.setCurrentWizardStepIndex(targetIndex)
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return saved
      } else {
        console.error('❌ Failed to save step data')
        alert('Failed to save your progress. Please try again.')
      }
      return null
    } catch (e: any) {
      console.error('❌ Failed saving step during navigation:', e)
      let errorMessage = e?.message || 'Unknown error occurred'
      errorMessage = errorMessage.replace(/^Not supported state:\s*/i, '')
      alert(errorMessage)
      return null
    }
  }

  /**
   * Handle target step navigation after page reload (for document upload)
   */
  handleTargetStepNavigation(steps: any[]): void {
    const targetStep = sessionStorage.getItem('targetStep')
    if (targetStep) {
      const stepIndex = parseInt(targetStep) - 1
      if (stepIndex >= 0 && stepIndex < steps.length) {
        this.setCurrentWizardStepIndex(stepIndex)
      }
      sessionStorage.removeItem('targetStep')
    }
  }

  /**
   * Check if a step is clickable based on completed mode restrictions and step completion
   */
  isStepClickable(stepIndex: number, enrollmentData?: any): boolean {
    const isRestrictedInCompletedMode = this.isCompletedMode && stepIndex < 8
    if (isRestrictedInCompletedMode) {
      return false
    }
    
    if (!enrollmentData) {
      return true
    }
    
    return true // Allow navigation to all steps
  }

  /**
   * Mark a step as completed
   * ✅ FIXED: Only updates tracking arrays, no nested step objects
   */
  markStepCompleted(stepIndex: number, enrollmentDataRef: React.MutableRefObject<any>): void {
    const currentData = enrollmentDataRef.current
    const completedSteps = currentData.completedSteps || []
    const skippedSteps = currentData.skippedSteps || []
    
    if (!completedSteps.includes(stepIndex)) {
      // ✅ FIXED: Only update tracking arrays
      enrollmentDataRef.current = {
        ...currentData,
        completedSteps: [...completedSteps, stepIndex],
        skippedSteps: skippedSteps.filter((step: number) => step !== stepIndex)
      }
    }
  }

  /**
   * Mark a step as skipped
   * ✅ FIXED: Only updates tracking arrays, no nested step objects
   */
  markStepSkipped(stepIndex: number, enrollmentDataRef: React.MutableRefObject<any>): void {
    const currentData = enrollmentDataRef.current
    const completedSteps = currentData.completedSteps || []
    const skippedSteps = currentData.skippedSteps || []
    
    if (!skippedSteps.includes(stepIndex)) {
      // ✅ FIXED: Only update tracking arrays
      enrollmentDataRef.current = {
        ...currentData,
        skippedSteps: [...skippedSteps, stepIndex],
        completedSteps: completedSteps.filter((step: number) => step !== stepIndex)
      }
    }
  }

  /**
   * Check if a step is completed
   * ✅ FIXED: Only checks tracking arrays, no nested step objects
   */
  isStepCompleted(stepIndex: number, enrollmentData?: any): boolean {
    if (!enrollmentData) return false
    const completedSteps = enrollmentData.completedSteps || []
    return completedSteps.includes(stepIndex)
  }

  /**
   * Check if a step is skipped
   * ✅ FIXED: Only checks tracking arrays, no nested step objects
   */
  isStepSkipped(stepIndex: number, enrollmentData?: any): boolean {
    if (!enrollmentData) return false
    const skippedSteps = enrollmentData.skippedSteps || []
    return skippedSteps.includes(stepIndex)
  }

  /**
   * Get step styling classes based on current state
   */
  getStepStylingClasses(
    stepIndex: number,
    enrollmentData: any
  ): {
    containerClasses: string
    circleClasses: string
    textClasses: string
    stepNumber: string | number
  } {
    const isRestrictedInCompletedMode = this.isCompletedMode && stepIndex < 8
    const isClickable = this.isStepClickable(stepIndex, enrollmentData)
    const completedSteps = enrollmentData?.completedSteps || []
    const skippedSteps = enrollmentData?.skippedSteps || []
    
    // ✅ FIXED: Only check tracking arrays
    const isCompleted = completedSteps.includes(stepIndex)
    const isSkipped = skippedSteps.includes(stepIndex)
    const isCurrentStep = stepIndex === this.currentWizardStepIndex
    const isReached = stepIndex <= this.currentWizardStepIndex
    
    const containerClasses = `flex flex-col items-center min-w-0 flex-shrink-0 ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}`
    
    let circleClasses = ''
    let stepNumber: string | number = stepIndex + 1
    
    if (isRestrictedInCompletedMode) {
      circleClasses = 'bg-red-200 text-red-600'
    } else if (isCompleted) {
      circleClasses = 'bg-primary-600 text-white'
    } else if (isCurrentStep) {
      circleClasses = 'bg-primary-600 text-white'
    } else if (isSkipped) {
      circleClasses = 'bg-[#4F46E5] text-white'
      stepNumber = (stepIndex + 1).toString()
    } else if (isReached) {
      circleClasses = 'bg-[#4F46E5] text-white'
      stepNumber = (stepIndex + 1).toString()
    } else {
      circleClasses = 'bg-gray-400 text-white'
    }
    
    circleClasses += ' w-6 lg:w-8 h-6 lg:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors'
    
    const textClasses = `text-xs mt-1 text-center max-w-16 lg:max-w-20 truncate ${
      isRestrictedInCompletedMode ? 'text-red-500' : isClickable ? 'text-accent-600' : 'text-accent-400'
    }`
    
    return {
      containerClasses,
      circleClasses,
      textClasses,
      stepNumber
    }
  }
}